import { Fragment } from 'react';
import { Dialog } from './dialog.js';

interface Shortcut {
  /** Alternatives, each a list of keys pressed together. */
  keys: readonly (readonly string[])[];
  action: string;
}

interface ShortcutGroup {
  heading: string;
  shortcuts: readonly Shortcut[];
}

/**
 * Every shortcut the app binds, by where it works.
 *
 * Kept by hand, so a new binding has to be added here too. The handlers live in
 * `useSearchShortcut`, `useShortcutsHelpShortcut`, `useEscapeToClose`, the recipe and
 * schedule routes, `PhotoLightbox` and `UrlImportField`.
 */
const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    heading: 'Anywhere',
    shortcuts: [
      { keys: [['Ctrl', 'K'], ['/']], action: 'Search recipes' },
      { keys: [['Ctrl', '?']], action: 'Show keyboard shortcuts' },
      { keys: [['Esc']], action: 'Close a dialog' },
    ],
  },
  {
    heading: 'Recipe opened from a list',
    shortcuts: [
      { keys: [['←'], ['K']], action: 'Previous recipe' },
      { keys: [['→'], ['J']], action: 'Next recipe' },
      { keys: [['Esc'], ['U']], action: 'Back to the list' },
    ],
  },
  {
    heading: 'Photos',
    shortcuts: [
      { keys: [['←'], ['K']], action: 'Previous photo' },
      { keys: [['→'], ['J']], action: 'Next photo' },
    ],
  },
  {
    heading: 'Schedule',
    shortcuts: [
      { keys: [['←'], ['K']], action: 'Previous month' },
      { keys: [['→'], ['J']], action: 'Next month' },
      { keys: [['T']], action: 'This month' },
    ],
  },
  {
    heading: 'Importing from URLs',
    shortcuts: [{ keys: [['Ctrl', 'Enter']], action: 'Import' }],
  },
];

/** Opened with Ctrl/Cmd-? from anywhere in the app shell. */
export function KeyboardShortcutsDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog onClose={onClose} className="shortcuts-dialog" aria-labelledby="shortcuts-title">
      <h2 id="shortcuts-title">Keyboard shortcuts</h2>

      {SHORTCUT_GROUPS.map((group) => (
        <section key={group.heading} className="shortcuts-group">
          <h3>{group.heading}</h3>
          <dl>
            {group.shortcuts.map((shortcut) => (
              <div key={shortcut.action} className="shortcuts-row">
                <dt>{shortcut.action}</dt>
                <dd>
                  {shortcut.keys.map((combo, index) => (
                    <Fragment key={combo.join('+')}>
                      {index > 0 && <span className="shortcuts-or">or</span>}
                      {combo.map((key) => (
                        <kbd key={key}>{key}</kbd>
                      ))}
                    </Fragment>
                  ))}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      <div className="row" style={{ marginTop: 18 }}>
        <span className="spacer" />
        <button type="button" className="btn" onClick={onClose}>
          Done
        </button>
      </div>
    </Dialog>
  );
}
